import type { Ref, ComputedRef } from 'vue'
import { useStorage } from '@vueuse/core'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import {
  buildOnboardingSteps,
  type OnboardingStatus,
  type OnboardingStep,
  type OnboardingStepId
} from '~/utils/onboarding'

type OnboardingState = {
  loaded: boolean;
  completedAt: string | null;
  dismissedSteps: string[];
  status: OnboardingStatus | null;
}

const TARGET_WAIT_MS = 5000

// waits until the page rendered the highlighted element (tours start right after a navigation)
async function waitForTarget (target: string): Promise<boolean> {
  const start = Date.now()
  while (Date.now() - start < TARGET_WAIT_MS) {
    if (document.querySelector(`[data-tour="${target}"]`)) {
      return true
    }
    await new Promise(resolve => setTimeout(resolve, 100))
  }
  return false
}

export const useOnboarding = (): {
  state: Ref<OnboardingState>;
  isActive: ComputedRef<boolean>;
  showChecklist: ComputedRef<boolean>;
  showWelcome: ComputedRef<boolean>;
  steps: ComputedRef<OnboardingStep[]>;
  load: () => Promise<void>;
  refreshStatus: () => Promise<void>;
  closeWelcome: (subscribeChangelog: boolean) => Promise<void>;
  dismissStep: (id: OnboardingStepId) => Promise<void>;
  hideForSession: () => void;
  complete: () => Promise<void>;
  restart: () => Promise<void>;
  startStepTour: (id: OnboardingStepId) => Promise<void>;
} => {
  const { $api, $logger, $toast } = useNuxtApp()
  const { mainRole, currentEnv } = useCurrentUserSession()
  const { serviceprovider } = useCurrentServiceprovider()
  const route = useRoute()

  const state = useState<OnboardingState>('onboarding', () => ({
    loaded: false,
    completedAt: null,
    dismissedSteps: [],
    status: null
  }))

  // only real serviceprovider users get the onboarding, admins impersonating a serviceprovider do not
  const isActive = computed(() => mainRole === 'serviceprovider' && state.value.loaded && state.value.completedAt === null)

  const showWelcome = computed(() => isActive.value && !state.value.dismissedSteps.includes('welcome'))

  // "ausblenden" in the checklist only hides it for this browser session, it shows up again after a new login
  // or in a new tab - finishing it for good is done in the account settings (or when all steps are done)
  const hiddenForSession = useStorage('onboarding-hidden', false, sessionStorage)
  const showChecklist = computed(() => isActive.value && !showWelcome.value && !hiddenForSession.value)

  function hideForSession (): void {
    hiddenForSession.value = true
  }

  const steps = computed(() => buildOnboardingSteps(serviceprovider.value.uuid, state.value.status, state.value.dismissedSteps))

  async function refreshStatus (): Promise<void> {
    if (!serviceprovider.value.uuid) { return }
    try {
      state.value.status = await $api.serviceprovider.getServiceproviderOnboardingStatus(serviceprovider.value.uuid)
    } catch (e) {
      $logger.error(e)
    }
  }

  async function load (): Promise<void> {
    if (mainRole !== 'serviceprovider' || state.value.loaded) { return }
    try {
      const user = await $api.user.getUser()
      state.value.completedAt = user.onboarding?.completedAt ?? null
      state.value.dismissedSteps = user.onboarding?.dismissedSteps ?? []
      state.value.loaded = true

      if (state.value.completedAt === null) {
        await refreshStatus()
      }
    } catch (e) {
      $logger.error(e)
    }
  }

  async function saveDismissedSteps (dismissedSteps: string[]): Promise<void> {
    state.value.dismissedSteps = dismissedSteps
    await $api.user.updateOnboarding({ dismissedSteps })
  }

  async function closeWelcome (subscribeChangelog: boolean): Promise<void> {
    await dismissStep('welcome')

    try {
      if (subscribeChangelog && !currentEnv.isSelfhosted && !currentEnv.isDemoEnv) {
        await $api.user.setChangelogSubscription({ subscribed: true })
        $toast.success('Changelog abonniert')
      }
    } catch (e) {
      $logger.error(e)
      $toast.error('Fehler', 'Changelog konnte nicht abonniert werden')
    }

    await refreshStatus()
  }

  async function dismissStep (id: OnboardingStepId): Promise<void> {
    if (state.value.dismissedSteps.includes(id)) { return }
    try {
      await saveDismissedSteps([...state.value.dismissedSteps, id])
    } catch (e) {
      $logger.error(e)
    }
  }

  async function complete (): Promise<void> {
    try {
      await $api.user.updateOnboarding({ completed: true })
      state.value.completedAt = new Date().toISOString()
    } catch (e) {
      $logger.error(e)
      $toast.error('Fehler', 'Onboarding konnte nicht ausgeblendet werden')
    }
  }

  async function restart (): Promise<void> {
    await $api.user.updateOnboarding({ completed: false })
    state.value.completedAt = null
    state.value.dismissedSteps = []
    state.value.loaded = true
    hiddenForSession.value = false
    await refreshStatus()
  }

  async function startStepTour (id: OnboardingStepId): Promise<void> {
    const step = steps.value.find(s => s.id === id)
    if (!step || step.blocked) { return }

    if (step.route && route.path !== step.route) {
      await navigateTo(step.route)
    }

    const available = []
    for (const tourStep of step.tour) {
      if (await waitForTarget(tourStep.target)) {
        available.push(tourStep)
      }
    }

    if (available.length === 0) {
      $logger.warn('Onboarding tour target not found')
      return
    }

    driver({
      showProgress: available.length > 1,
      progressText: '{{current}} von {{total}}',
      nextBtnText: 'Weiter',
      prevBtnText: 'Zurück',
      doneBtnText: 'Fertig',
      popoverClass: 'sec-tour',
      steps: available.map(t => ({
        element: `[data-tour="${t.target}"]`,
        popover: { title: t.title, description: t.description }
      })),
      onDestroyed: () => {
        // explanation-only steps are done once the tour was watched
        if (id === 'modes') {
          dismissStep(id)
        }
      }
    }).drive()
  }

  return { state, isActive, showChecklist, showWelcome, steps, load, refreshStatus, closeWelcome, dismissStep, hideForSession, complete, restart, startStepTour }
}
